import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { apiFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, timeAgo, STATUSES, PRIORITIES, CATEGORIES } from '../utils/helpers';
import useIsMobile from '../utils/useIsMobile';
import TicketAttachments from '../components/TicketAttachments';
import { useFeatures } from '../utils/useFeatures';

// Is this agent currently out of office?
function DetailChip({ label, value }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--bg-app)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '4px 12px' }}>
      <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{label}:</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}

function isAgentOOF(a) {
  if (!a?.oof_from || !a?.oof_to) return false;
  const today = new Date().toISOString().slice(0, 10);
  return today >= a.oof_from && today <= a.oof_to;
}

function InternalAgentChat({ currentUser, token }) {
  const [staff, setStaff]         = useState([]);
  const [peerId, setPeerId]       = useState('');
  const [chatOpen, setChatOpen]   = useState(false);
  const [msgs, setMsgs]           = useState([]);
  const [input, setInput]         = useState('');
  const [incomingFrom, setIncomingFrom] = useState(null); // { fromId, fromName }
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  // Load all managers + admins from API
  useEffect(() => {
    apiFetch('/users/agents')
      .then(list => setStaff((list || []).filter(u => u.id !== currentUser.id)))
      .catch(() => {});
  }, [currentUser.id]);

  // Socket setup
  useEffect(() => {
    const sock = io('/', { auth: { token }, transports: ['websocket', 'polling'] });
    socketRef.current = sock;

    sock.on('dm:message', (msg) => {
      // Show message only if it's from our current peer or is our own sent message
      setMsgs(prev => [...prev, msg]);
      // If not in chat yet and it's from someone else, show notification
      if (!chatOpen && msg.fromId !== currentUser.id) {
        setIncomingFrom({ fromId: msg.fromId, fromName: msg.fromName });
      }
    });

    return () => sock.disconnect();
  }, [currentUser.id, chatOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs]);

  function openChat() {
    if (!peerId) return;
    setMsgs([]);
    setChatOpen(true);
    setIncomingFrom(null);
  }

  function closeChat() {
    setChatOpen(false);
    setMsgs([]);
    setPeerId('');
  }

  function acceptIncoming() {
    setPeerId(incomingFrom.fromId);
    setChatOpen(true);
    setIncomingFrom(null);
  }

  function send() {
    const txt = input.trim();
    if (!txt || !peerId) return;
    socketRef.current?.emit('dm:send', { toId: peerId, content: txt });
    setInput('');
  }

  const peer = staff.find(u => u.id === peerId);
  const ROLE_COLOR = { admin: '#7c3aed', manager: '#ea580c', agent: '#0B6623' };

  // Filter messages relevant to current conversation
  const convMsgs = msgs.filter(m =>
    (m.fromId === currentUser.id && m.toId === peerId) ||
    (m.fromId === peerId && m.toId === currentUser.id)
  );

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 16, border: '1px solid var(--border-subtle)' }}>
      {/* Header */}
      <div style={{ padding: '10px 14px', background: '#3a4a36', display: 'flex', alignItems: 'center', gap: 8 }}>
        {chatOpen && (
          <button onClick={closeChat} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 15, padding: 0, lineHeight: 1 }}>←</button>
        )}
        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', flex: 1 }}>
          {chatOpen && peer ? `Chat with ${peer.name}` : 'Team Chat'}
        </span>
        {chatOpen && peer && (
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'capitalize', color: ROLE_COLOR[peer.role] || '#94a3b8', background: '#2a3826', padding: '2px 8px', borderRadius: 99 }}>
            {peer.role}
          </span>
        )}
      </div>

      {/* Incoming message notification */}
      {incomingFrom && !chatOpen && (
        <div style={{ padding: '8px 12px', background: 'var(--amber-100)', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>
            💬 Message from {incomingFrom.fromName}
          </span>
          <button onClick={acceptIncoming} style={{ padding: '3px 10px', background: '#0B6623', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
            Open
          </button>
        </div>
      )}

      {/* Select person + start — shown when chat not open */}
      {!chatOpen && (
        <div style={{ padding: '12px 14px', background: 'var(--bg-app)', borderBottom: '1px solid var(--n-100)' }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .4 }}>
            Select person to chat with
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              className="input"
              value={peerId}
              onChange={e => setPeerId(e.target.value)}
              style={{ flex: 1, minWidth: 0, fontSize: 12, padding: '6px 8px' }}
            >
              <option value="">— Select agent / manager —</option>
              {staff.filter(u => ['manager','admin'].includes(u.role)).length > 0 && (
                <optgroup label="── Managers / Admin ──">
                  {staff.filter(u => ['manager','admin'].includes(u.role)).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </optgroup>
              )}
              {staff.filter(u => u.role === 'agent').length > 0 && (
                <optgroup label="── Agents ──">
                  {staff.filter(u => u.role === 'agent').map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              onClick={openChat}
              disabled={!peerId}
              style={{ padding: '6px 12px', background: peerId ? 'var(--status-resolved-text)' : 'var(--n-200)', color: peerId ? '#fff' : '#94a3b8', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: peerId ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
            >
              Start
            </button>
          </div>
        </div>
      )}

      {/* Chat messages */}
      {chatOpen && (
        <>
          <div style={{ height: 200, overflowY: 'auto', padding: '10px 12px', background: 'var(--bg-app)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {convMsgs.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, marginTop: 60 }}>
                Say hi to {peer?.name || 'them'}
              </div>
            )}
            {convMsgs.map(m => {
              const isMine = m.fromId === currentUser.id;
              return (
                <div key={m.id} style={{ display: 'flex', flexDirection: isMine ? 'row-reverse' : 'row', gap: 6, alignItems: 'flex-end' }}>
                  <div style={{ maxWidth: '80%' }}>
                    <div style={{ background: isMine ? '#3a4a36' : 'var(--bg-card)', color: isMine ? '#fff' : 'var(--text-main)', padding: '7px 11px', borderRadius: isMine ? '10px 10px 2px 10px' : '10px 10px 10px 2px', fontSize: 12, lineHeight: 1.5, border: isMine ? 'none' : '1px solid var(--border)', wordBreak: 'break-word' }}>
                      {m.content}
                    </div>
                    <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2, textAlign: isMine ? 'right' : 'left' }}>
                      {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 6, background: 'var(--bg-surface)' }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
              placeholder={`Message ${peer?.name || ''}…`}
              style={{ flex: 1, fontSize: 12, padding: '6px 10px', border: '1px solid var(--border-subtle)', borderRadius: 8, outline: 'none' }}
              autoFocus
            />
            <button
              onClick={send}
              disabled={!input.trim()}
              style={{ padding: '6px 12px', background: '#3a4a36', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: input.trim() ? 'pointer' : 'default', opacity: input.trim() ? 1 : 0.4 }}
            >
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Inline FAQ Panel ──────────────────────────────────────────────────────────
function FAQPanel({ onClose, productId, productName }) {
  const [allFaqs, setAllFaqs]   = useState([]);
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const params = productId ? `?product_id=${productId}` : '';
    apiFetch(`/knowledge/faq${params}`).then(d => setAllFaqs(d || [])).catch(() => {});
  }, [productId]);

  const faqs = allFaqs.filter(f => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return f.question?.toLowerCase().includes(q) || f.answer?.toLowerCase().includes(q) || f.meta?.toLowerCase().includes(q);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#0B6623', borderRadius: '12px 12px 0 0', flexShrink: 0 }}>
        <div>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>📖 FAQ Quick Reference</span>
          {productName && <div style={{ fontSize: 11, color: '#bbf7d0', marginTop: 1 }}>{productName}</div>}
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,.2)', border: 'none', borderRadius: 6, color: '#fff', fontSize: 16, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontWeight: 700 }}>×</button>
      </div>
      {/* Search */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0, background: 'var(--bg-app)' }}>
        <input
          className="input"
          style={{ fontSize: 12, padding: '6px 10px' }}
          placeholder="Search FAQs…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {faqs.length === 0 ? (
          <div style={{ padding: '30px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            {search ? 'No FAQs match your search.' : 'No FAQs available.'}
          </div>
        ) : faqs.map(f => (
          <div key={f.id} style={{ borderBottom: '1px solid var(--n-100)' }}>
            <button
              onClick={() => setExpanded(expanded === f.id ? null : f.id)}
              style={{ width: '100%', textAlign: 'left', padding: '10px 14px', background: expanded === f.id ? 'var(--action-bg)' : 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 5, marginBottom: 3, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'capitalize', background: 'var(--action-bg)', color: 'var(--status-resolved-text)', padding: '1px 6px', borderRadius: 99 }}>{f.category}</span>
                  {f.product_name && <span style={{ fontSize: 10, background: 'var(--blue-50)', color: 'var(--blue-700)', padding: '1px 6px', borderRadius: 99, fontWeight: 600 }}>{f.product_name}</span>}
                </div>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-main)', margin: 0, lineHeight: 1.4 }}>Q: {f.question}</p>
              </div>
              <span style={{ color: '#94a3b8', fontSize: 12, flexShrink: 0 }}>{expanded === f.id ? '▲' : '▼'}</span>
            </button>
            {expanded === f.id && (
              <div style={{ padding: '0 14px 12px', background: 'var(--action-bg)', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                A: {f.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const { user, token, isAgent, isManager, isAdmin } = useAuth();
  const nav = useNavigate();
  const _loc = useLocation();
  const basePath = _loc.pathname.startsWith('/manager') ? '/manager/tickets' : _loc.pathname.startsWith('/agent') ? '/agent/tickets' : '/admin/tickets';
  const isMobile = useIsMobile();
  const { feature } = useFeatures();
  const [ticket, setTicket] = useState(null);
  const [agents, setAgents] = useState([]);           // all agents
  const [capableAgents, setCapableAgents] = useState([]); // agents who handle this ticket's product
  const [ticketProductId, setTicketProductId] = useState(null); // DB id of ticket's product
  const [msg, setMsg] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [remarks, setRemarks] = useState('');       // full stored remarks history
  const [newRemark, setNewRemark] = useState('');   // new entry being typed
  const [remarksSaving, setRemarksSaving] = useState(false);
  const [remarksSaved, setRemarksSaved] = useState(false);
  const [nextTicketId, setNextTicketId] = useState(null);

  // Keyboard nav refs
  const statusRef     = useRef(null);
  const remarkRef     = useRef(null);
  const backBtnRef    = useRef(null);
  const nextBtnRef    = useRef(null);
  const didAutoFocus  = useRef(false);

  const [showFaq, setShowFaq] = useState(false);

  // SLA clock stop/resume (staff)
  const [showSlaStop, setShowSlaStop] = useState(false);
  const [slaConfirm, setSlaConfirm] = useState('');
  const [slaReason, setSlaReason] = useState('');
  const [slaBusy, setSlaBusy] = useState(false);
  const [slaErr, setSlaErr] = useState('');
  async function stopSla(e) {
    e.preventDefault();
    if (slaConfirm.trim().toUpperCase() !== 'STOP') { setSlaErr('Type STOP to confirm.'); return; }
    setSlaBusy(true); setSlaErr('');
    try {
      await apiFetch(`/tickets/${id}/sla/stop`, { method: 'POST', body: { confirm: slaConfirm.trim(), reason: slaReason.trim() } });
      setShowSlaStop(false); setSlaConfirm(''); setSlaReason('');
      await load();
    } catch (err) { setSlaErr(err.message); } finally { setSlaBusy(false); }
  }
  async function resumeSlaAction() {
    setSlaBusy(true);
    try { await apiFetch(`/tickets/${id}/sla/resume`, { method: 'POST' }); await load(); }
    catch (err) { alert(err.message); } finally { setSlaBusy(false); }
  }

  // Reopen (customer side)
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenBusy, setReopenBusy] = useState(false);
  const [reopenErr, setReopenErr] = useState('');
  async function handleReopen(e) {
    e.preventDefault();
    if (!reopenReason.trim()) { setReopenErr('Please enter a reason.'); return; }
    setReopenBusy(true); setReopenErr('');
    try {
      await apiFetch(`/tickets/${id}/reopen`, { method: 'POST', body: { reason: reopenReason.trim() } });
      setShowReopen(false); setReopenReason('');
      await load();
    } catch (err) { setReopenErr(err.message); } finally { setReopenBusy(false); }
  }

  const isPureAgent = user?.role === 'agent';

  // Delete-ticket modal (super admin)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');
  async function handleDeleteTicket(e) {
    e.preventDefault();
    if (!deleteReason.trim()) { setDeleteErr('A reason is required.'); return; }
    setDeleteBusy(true); setDeleteErr('');
    try {
      await apiFetch(`/tickets/${id}/delete`, { method: 'POST', body: { reason: deleteReason.trim() } });
      nav(basePath);
    } catch (err) { setDeleteErr(err.message); setDeleteBusy(false); }
  }

  // Close-ticket modal
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [closeRemark, setCloseRemark] = useState('');
  const [resolutionOutcome, setResolutionOutcome] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [rmaRef, setRmaRef] = useState('');
  const [customerConfirmed, setCustomerConfirmed] = useState(1);
  const [timeSpent, setTimeSpent] = useState('');
  const [preventiveAction, setPreventiveAction] = useState('');
  const RESOLUTION_OUTCOMES = ['Fixed', 'Workaround given', 'Config change', 'Device replaced (RMA)', 'No fault found', 'Cannot reproduce', 'Duplicate', 'Rejected'];
  const [closeError, setCloseError] = useState('');
  const [closeSaving, setCloseSaving] = useState(false);
  // FAQ submission inside close modal
  const [addToFaq, setAddToFaq] = useState(false);
  const [faqQuestion, setFaqQuestion] = useState('');
  const [faqAnswer, setFaqAnswer] = useState('');

  // Sub-tickets
  const [subTickets, setSubTickets]         = useState([]);
  const [showSubForm, setShowSubForm]       = useState(false);
  const [subForm, setSubForm]               = useState({ title: '', description: '', assigned_to: '' });
  const [subSaving, setSubSaving]           = useState(false);
  const [expandedSub, setExpandedSub]       = useState(null);
  const [subMessages, setSubMessages]       = useState({});
  const [subMsg, setSubMsg]                 = useState('');
  const [subStatus, setSubStatus]           = useState({});

  const CLOSE_REASONS = [
    { value: 'technical',      label: '🔧 Technical' },
    { value: 'configuration',  label: '⚙️ Configuration' },
    { value: 'software',       label: '💻 Software' },
    { value: 'hardware',       label: '🖥️ Hardware' },
    { value: 'other',          label: '📋 Other' },
  ];

  async function loadSubTickets() {
    try {
      const subs = await apiFetch(`/sub-tickets/ticket/${id}`);
      setSubTickets(subs || []);
    } catch (_) {}
  }

  async function load() {
    const t = await apiFetch(`/tickets/${id}`);
    setTicket(t);
    setRemarks(t.remarks || '');
    loadSubTickets();
    if (isAgent) {
      const a = await apiFetch('/users/agents');
      setAgents(a);
      // Filter to agents who handle this product (if product is set)
      if (t.product_name) {
        // Filter to agents who handle this product (agent_products is [{product_id, tier}])
        apiFetch('/products').then(products => {
          const match = products.find(p => p.name === t.product_name);
          if (match) {
            setTicketProductId(match.id);
            setCapableAgents(a.filter(ag =>
              Array.isArray(ag.agent_products) &&
              ag.agent_products.some(e => e.product_id === match.id)
            ));
          } else {
            setTicketProductId(null);
            setCapableAgents(a);
          }
        }).catch(() => setCapableAgents(a));
      } else {
        setCapableAgents(a);
      }
    }
  }

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [id]);

  // Auto-focus status field for pure agents — only once on initial load
  useEffect(() => {
    if (!loading && ticket && isPureAgent && !didAutoFocus.current) {
      didAutoFocus.current = true;
      setTimeout(() => statusRef.current?.focus(), 80);
    }
  }, [loading, ticket, isPureAgent]);

  // Load next open/in-progress ticket in agent's queue
  useEffect(() => {
    if (!isPureAgent) return;
    // Fetch open tickets then in_progress — backend filters to this agent automatically
    Promise.all([
      apiFetch('/tickets?status=open&limit=200').catch(() => ({ tickets: [] })),
      apiFetch('/tickets?status=in_progress&limit=200').catch(() => ({ tickets: [] })),
    ]).then(([a, b]) => {
      const list = [...(a.tickets || []), ...(b.tickets || [])];
      // sort by created_at ascending so order matches the ticket list
      list.sort((x, y) => new Date(x.created_at) - new Date(y.created_at));
      const idx = list.findIndex(t => String(t.id) === String(id));
      if (idx !== -1 && idx + 1 < list.length) setNextTicketId(list[idx + 1].id);
      else setNextTicketId(null);
    }).catch(() => setNextTicketId(null));
  }, [id, isPureAgent]);

  async function sendMessage(e) {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      const newMsg = await apiFetch(`/tickets/${id}/messages`, { method: 'POST', body: { content: msg, is_internal: isInternal } });
      setTicket(t => ({ ...t, messages: [...t.messages, newMsg] }));
      setMsg('');
    } finally {
      setSending(false);
    }
  }

  async function updateField(field, value) {
    // Intercept status → closed: show the close modal instead
    if (field === 'status' && value === 'closed') {
      setCloseReason('');
      setCloseRemark('');
      setResolutionOutcome(''); setRootCause(''); setRmaRef(''); setCustomerConfirmed(1); setTimeSpent(''); setPreventiveAction('');
      setCloseError('');
      setShowCloseModal(true);
      return;
    }
    const updated = await apiFetch(`/tickets/${id}`, { method: 'PATCH', body: { [field]: value } });
    setTicket(t => ({ ...t, ...updated }));
  }

  function faqWordCount(text) {
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  }

  async function handleCloseTicket(e) {
    e.preventDefault();
    if (!resolutionOutcome) { setCloseError('Please select a resolution outcome.'); return; }
    if (!closeReason) { setCloseError('Please select a resolution category.'); return; }
    if (!rootCause.trim()) { setCloseError('Please enter the root cause.'); return; }
    if (!closeRemark.trim()) { setCloseError('Please enter a closing remark.'); return; }
    if (addToFaq) {
      if (!faqQuestion.trim()) { setCloseError('Please enter a FAQ question.'); return; }
      if (!faqAnswer.trim()) { setCloseError('Please enter a FAQ answer.'); return; }
      if (faqWordCount(faqQuestion) > 50) { setCloseError('FAQ question must be 50 words or fewer.'); return; }
      if (faqWordCount(faqAnswer) > 200) { setCloseError('FAQ answer must be 200 words or fewer.'); return; }
    }
    setCloseSaving(true);
    setCloseError('');
    try {
      const updated = await apiFetch(`/tickets/${id}`, {
        method: 'PATCH',
        body: { status: 'closed', close_reason: closeReason, close_remark: closeRemark.trim(),
          resolution_outcome: resolutionOutcome, root_cause: rootCause.trim(), rma_ref: rmaRef.trim(),
          customer_confirmed: customerConfirmed, time_spent: timeSpent.trim(), preventive_action: preventiveAction.trim() },
      });
      setTicket(t => ({ ...t, ...updated }));
      if (addToFaq && faqQuestion.trim() && faqAnswer.trim()) {
        await apiFetch('/knowledge/faq/submit', {
          method: 'POST',
          body: { question: faqQuestion.trim(), answer: faqAnswer.trim(), ticket_id: id, category: closeReason || 'technical', product_name: ticket.product_name || '' },
        });
      }
      setShowCloseModal(false);
      setAddToFaq(false); setFaqQuestion(''); setFaqAnswer('');
    } catch (err) {
      setCloseError(err.message);
    } finally {
      setCloseSaving(false);
    }
  }

  async function saveRemarks(onDone) {
    if (!newRemark.trim()) return;
    setRemarksSaving(true);
    setRemarksSaved(false);
    try {
      // Format: [DD/MM/YYYY HH:MM | Agent Name]\nremark text\n\n
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      const header = `[${dd}/${mm}/${yyyy} ${hh}:${min} | ${user.name}]`;
      const entry = `${header}\n${newRemark.trim()}\n\n`;
      const appended = remarks ? remarks + entry : entry;
      await apiFetch(`/tickets/${id}`, { method: 'PATCH', body: { remarks: appended } });
      setRemarks(appended);
      setNewRemark('');
      setRemarksSaved(true);
      setTimeout(() => setRemarksSaved(false), 2500);
      if (onDone) onDone();
    } finally {
      setRemarksSaving(false);
    }
  }

  // Parse remarks string into individual entries for display
  function parseRemarks(raw) {
    if (!raw || !raw.trim()) return [];
    // Split on lines that start with [DD/MM/YYYY
    const entries = [];
    const lines = raw.split('\n');
    let current = null;
    for (const line of lines) {
      if (/^\[\d{2}\/\d{2}\/\d{4}/.test(line)) {
        if (current) entries.push(current);
        current = { header: line, body: '' };
      } else if (current) {
        current.body = (current.body + '\n' + line).trimStart();
      }
    }
    if (current) entries.push(current);
    return entries;
  }

  async function createSubTicket(e) {
    e.preventDefault();
    setSubSaving(true);
    try {
      const sub = await apiFetch('/sub-tickets', { method: 'POST', body: { parent_ticket_id: id, ...subForm } });
      setSubTickets(s => [...s, sub]);
      setSubForm({ title: '', description: '', assigned_to: '' });
      setShowSubForm(false);
    } catch (err) { alert(err.message); }
    setSubSaving(false);
  }

  async function loadSubMessages(subId) {
    const msgs = await apiFetch(`/sub-tickets/${subId}/messages`);
    setSubMessages(m => ({ ...m, [subId]: msgs }));
  }

  async function sendSubMessage(subId) {
    if (!subMsg.trim()) return;
    const msg = await apiFetch(`/sub-tickets/${subId}/messages`, { method: 'POST', body: { content: subMsg } });
    setSubMessages(m => ({ ...m, [subId]: [...(m[subId] || []), msg] }));
    setSubMsg('');
  }

  async function updateSubStatus(subId, status) {
    await apiFetch(`/sub-tickets/${subId}`, { method: 'PATCH', body: { status } });
    setSubTickets(s => s.map(t => t.id === subId ? { ...t, status } : t));
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><span className="spinner" /></div>;
  if (!ticket) return <div style={{ padding: 40, textAlign: 'center' }}>Ticket not found.</div>;

  const slaBreached = ticket.due_at && new Date(ticket.due_at) < new Date() && !['resolved','closed'].includes(ticket.status) && !ticket.sla_paused;

  return (
    <>
    <div className="page-sm">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            ref={backBtnRef}
            className="btn btn-secondary btn-sm"
            onClick={() => nav(-1)}
            onKeyDown={e => { if (e.key === 'Tab' && !e.shiftKey && nextBtnRef.current) { e.preventDefault(); nextBtnRef.current.focus(); } }}
          >← Back</button>
          {isPureAgent && (
            <>
              <button
                ref={nextBtnRef}
                className="btn btn-secondary btn-sm"
                disabled={!nextTicketId}
                onClick={() => nextTicketId && nav(`/agent/tickets/${nextTicketId}`)}
                style={{ opacity: nextTicketId ? 1 : 0.45 }}
              >
                Next →
              </button>
            </>
          )}
        </div>
        {isAgent && feature('knowledge.enabled') && (
          <button
            onClick={() => setShowFaq(f => !f)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: showFaq ? '1.5px solid var(--status-resolved-text)' : '1.5px solid var(--border-subtle)', background: showFaq ? 'var(--action-bg)' : 'var(--bg-surface)', color: showFaq ? 'var(--status-resolved-text)' : 'var(--text-secondary)', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all .15s' }}
          >
            📖 FAQ
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: isMobile ? 16 : 24, alignItems: 'start' }}>
        {/* Main column */}
        <div style={{ minWidth: 0 }}>
          <div className="card" style={{ padding: 28, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ background: '#0B6623', color: '#fff', borderRadius: 6, padding: '3px 12px', fontSize: 13, fontWeight: 700, letterSpacing: .5, fontFamily: 'monospace' }}>
                    {ticket.ticket_number}
                  </span>
                  <span className={`badge badge-${ticket.status}`}>{ticket.status.replace('_',' ')}</span>
                  <span className={`badge badge-${ticket.priority}`}>{ticket.priority}</span>
                </div>
                <h1 style={{ fontSize: 20, fontWeight: 700 }}>{ticket.title}</h1>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                  Opened by {ticket.customer_name} · {timeAgo(ticket.created_at)}
                  {ticket.agent_name && ` · Assigned to ${ticket.agent_name}`}
                </p>
                {ticket.product_name && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 8, padding: '4px 12px' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Product:</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--status-resolved-text)' }}>{ticket.product_name}</span>
                  </div>
                )}
                {(ticket.issue_type || ticket.location || ticket.contact_name || ticket.contact_mobile || ticket.started_on || ticket.is_recurring) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {ticket.issue_type && <DetailChip label="Issue" value={ticket.issue_type} />}
                    {ticket.location && <DetailChip label="📍 Location" value={ticket.location} />}
                    {ticket.contact_name && <DetailChip label="👤 Contact" value={ticket.contact_name} />}
                    {ticket.contact_mobile && <DetailChip label="📞 Mobile" value={ticket.contact_mobile} />}
                    {ticket.started_on && <DetailChip label="Started" value={ticket.started_on} />}
                    {!!ticket.is_recurring && <DetailChip label="Recurring" value="Yes" />}
                  </div>
                )}
              </div>
            </div>
            <div style={{ background: 'var(--bg-app)', borderRadius: 8, padding: 16, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {ticket.description}
            </div>
            {slaBreached && (
              <div style={{ marginTop: 12, background: '#fee2e2', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#b91c1c', fontWeight: 500 }}>
                ⚠ SLA breached — due {formatDate(ticket.due_at)}
              </div>
            )}
            {ticket.due_at && !slaBreached && (
              <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}>Due by {formatDate(ticket.due_at)}</div>
            )}
          </div>

          {/* Reopen — customer side, closed tickets */}
          {ticket.status === 'closed' && ['customer', 'customer_manager'].includes(user.role) && ticket.reopen && feature('customer_reopen.enabled') && (
            <div className="card" style={{ padding: 18, marginBottom: 20, borderLeft: '4px solid #0B6623' }}>
              {ticket.reopen.allowed ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>Not resolved? Reopen this ticket</div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                      You can reopen within {ticket.reopen.window_days} day{ticket.reopen.window_days === 1 ? '' : 's'} of closing.
                    </div>
                  </div>
                  <button className="btn btn-primary" style={{ background: '#0B6623' }} onClick={() => { setReopenErr(''); setShowReopen(true); }}>🔄 Reopen ticket</button>
                </div>
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  {ticket.reopen.reason === 'expired'
                    ? `The reopen window (${ticket.reopen.window_days} days) has passed. Please raise a new ticket if the issue persists.`
                    : ticket.reopen.reason === 'disabled'
                    ? 'Reopening tickets is currently disabled. Please raise a new ticket if the issue persists.'
                    : 'This ticket can no longer be reopened. Please raise a new ticket if needed.'}
                </div>
              )}
            </div>
          )}

          {/* SLA clock — stop when pending with client (staff only) */}
          {isAgent && !['resolved', 'closed'].includes(ticket.status) && (feature('sla_stop.enabled') || ticket.sla_paused) && (
            ticket.sla_paused ? (
              <div className="card" style={{ padding: 16, marginBottom: 20, borderLeft: '4px solid #d97706', background: 'var(--amber-50)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>⏸ SLA stopped — pending with client</div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>
                      Stopped {ticket.sla_paused_at ? timeAgo(ticket.sla_paused_at) : ''}{ticket.sla_paused_reason ? ` · ${ticket.sla_paused_reason}` : ''}. The clock is frozen; it resumes automatically when the customer replies.
                    </div>
                  </div>
                  <button className="btn btn-primary" style={{ background: '#0B6623' }} disabled={slaBusy} onClick={resumeSlaAction}>▶ Resume SLA</button>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setSlaErr(''); setSlaConfirm(''); setShowSlaStop(true); }}
                  style={{ color: '#92400e', background: 'var(--amber-50)', border: '1px solid var(--amber-100)' }}>
                  ⏸ Stop SLA (pending with client)
                </button>
              </div>
            )
          )}

          {/* Screenshots — visible to everyone on the ticket */}
          {feature('attachments.enabled') && (
          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <TicketAttachments
              ticketId={ticket.id}
              attachments={ticket.attachments || []}
              canEdit={true}
              isStaff={isAgent}
              currentUserId={user.id}
              onChange={load}
            />
          </div>
          )}

          {/* Remarks — visible to agent/admin only */}
          {isAgent && (
            <div className="card" style={{ padding: 24, marginBottom: 20, borderLeft: '4px solid var(--status-resolved-text)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div>
                  <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--status-resolved-text)' }}>📝 Action Taken / Remarks</h2>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Running log of actions taken (visible to agents &amp; admin only)</p>
                </div>
                {remarksSaved && (
                  <span style={{ fontSize: 12, color: 'var(--status-resolved-text)', background: 'var(--action-bg)', padding: '4px 12px', borderRadius: 999, fontWeight: 600 }}>
                    ✓ Remark Added
                  </span>
                )}
              </div>

              {/* History log */}
              {(() => {
                const entries = parseRemarks(remarks);
                if (entries.length === 0) return (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14, fontStyle: 'italic' }}>No remarks yet. Add the first one below.</p>
                );
                return (
                  <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {entries.map((e, i) => (
                      <div key={i} style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--status-resolved-text)', marginBottom: 4, fontFamily: 'monospace', letterSpacing: 0.2 }}>
                          {e.header}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {e.body.trim()}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Add new remark */}
              <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                  Add New Remark
                </label>
                <textarea
                  ref={remarkRef}
                  className="input"
                  rows={3}
                  placeholder="Describe the action taken, steps performed, parts replaced, follow-up required, etc…"
                  value={newRemark}
                  onChange={e => { setNewRemark(e.target.value); setRemarksSaved(false); }}
                  onKeyDown={e => {
                    if (e.key === 'Tab' && newRemark.trim()) {
                      e.preventDefault();
                      backBtnRef.current?.focus();
                      saveRemarks();
                    }
                  }}
                  style={{ resize: 'vertical', fontSize: 14, lineHeight: 1.6, marginBottom: 10 }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-primary"
                    onClick={saveRemarks}
                    disabled={remarksSaving || !newRemark.trim()}
                    style={{ minWidth: 130 }}
                  >
                    {remarksSaving ? 'Saving…' : '+ Add Remark'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="card" style={{ padding: 24, marginBottom: 20 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Conversation</h2>
            {ticket.messages?.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>No messages yet.</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {ticket.messages?.map(m => (
                <div key={m.id} style={{ display: 'flex', gap: 12, flexDirection: m.sender_id === user.id ? 'row-reverse' : 'row' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: m.sender_role === 'customer' ? 'var(--blue-100)' : 'var(--action-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                    {m.sender_name?.[0]?.toUpperCase()}
                  </div>
                  <div style={{ maxWidth: '70%' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, textAlign: m.sender_id === user.id ? 'right' : 'left' }}>
                      {m.sender_name} · {timeAgo(m.created_at)}
                      {m.is_internal ? <span style={{ marginLeft: 6, background: 'var(--amber-100)', color: '#92400e', padding: '1px 6px', borderRadius: 4 }}>Internal</span> : null}
                    </div>
                    <div style={{ background: m.sender_id === user.id ? 'var(--action-bg)' : 'var(--n-100)', padding: '10px 14px', borderRadius: 10, fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                      {m.content}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!['closed'].includes(ticket.status) && (
              <form onSubmit={sendMessage} style={{ marginTop: 20 }}>
                {isAgent && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
                    Internal note (not visible to customer)
                  </label>
                )}
                <textarea className="input" rows={3} placeholder="Type your reply…" value={msg}
                  onChange={e => setMsg(e.target.value)} style={{ resize: 'vertical', marginBottom: 10 }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-primary" disabled={sending || !msg.trim()}>
                    {sending ? 'Sending…' : 'Send Reply'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* History */}
          {isAgent && ticket.history?.length > 0 && (
            <div className="card" style={{ padding: 24, marginBottom: 20 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Activity Log</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {ticket.history.map(h => (
                  <div key={h.id} style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', gap: 8 }}>
                    <span>{formatDate(h.created_at)}</span>
                    <span>·</span>
                    <span><strong>{h.changed_by_name}</strong> changed <em>{h.field}</em> from "{h.old_value}" to "{h.new_value}"</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sub-Tickets */}
          {isAgent && feature('subtickets.enabled') && (
            <div className="card" style={{ padding: 24, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <h2 style={{ fontSize: 15, fontWeight: 700 }}>Sub-Tickets</h2>
                  {ticket.due_at && (
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      SLA deadline inherited from parent: {formatDate(ticket.due_at)}
                    </p>
                  )}
                </div>
                {(isManager || isAdmin) && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowSubForm(f => !f)}
                  >
                    {showSubForm ? 'Cancel' : '+ Break into Sub-ticket'}
                  </button>
                )}
              </div>

              {/* Create sub-ticket form */}
              {showSubForm && (isManager || isAdmin) && (
                <div style={{ background: 'var(--bg-app)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 18, marginBottom: 18 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 14, color: 'var(--text-muted)' }}>New Sub-Ticket</h3>
                  <div className="form-group">
                    <label className="label">Title *</label>
                    <input
                      className="input"
                      placeholder="Sub-ticket title"
                      value={subForm.title}
                      onChange={e => setSubForm(f => ({ ...f, title: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Description *</label>
                    <textarea
                      className="input"
                      rows={3}
                      placeholder="Describe what needs to be done..."
                      value={subForm.description}
                      onChange={e => setSubForm(f => ({ ...f, description: e.target.value }))}
                      style={{ resize: 'vertical' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label className="label">Assign To</label>
                    <select
                      className="input"
                      value={subForm.assigned_to}
                      onChange={e => setSubForm(f => ({ ...f, assigned_to: e.target.value }))}
                    >
                      <option value="">— Unassigned —</option>
                      {agents.map(a => <option key={a.id} value={a.id}>{a.name}{isAgentOOF(a) ? "  🌴 OOF" : ""}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowSubForm(false)}>Cancel</button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={createSubTicket}
                      disabled={subSaving || !subForm.title.trim() || !subForm.description.trim()}
                    >
                      {subSaving ? 'Creating…' : 'Create Sub-Ticket'}
                    </button>
                  </div>
                </div>
              )}

              {/* Sub-ticket list */}
              {subTickets.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                  No sub-tickets yet.{(isManager || isAdmin) ? ' Use the button above to break this ticket into sub-tasks.' : ''}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {subTickets.map(sub => (
                    <div key={sub.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden' }}>
                      {/* Sub-ticket header */}
                      <div
                        style={{ padding: '12px 16px', background: 'var(--bg-app)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
                        onClick={() => {
                          if (expandedSub === sub.id) { setExpandedSub(null); }
                          else { setExpandedSub(sub.id); loadSubMessages(sub.id); }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, background: '#0B6623', color: '#fff', padding: '2px 8px', borderRadius: 4 }}>
                            {sub.ticket_number}
                          </span>
                          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{sub.title}</span>
                          {sub.assigned_name && (
                            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>→ {sub.assigned_name}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className={`badge badge-${sub.status}`} style={{ fontSize: 11 }}>
                            {sub.status.replace('_',' ')}
                          </span>
                          <span style={{ fontSize: 16, color: 'var(--text-secondary)' }}>{expandedSub === sub.id ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {/* Expanded sub-ticket body */}
                      {expandedSub === sub.id && (
                        <div style={{ padding: 16 }}>
                          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, whiteSpace: 'pre-wrap', marginBottom: 14 }}>
                            {sub.description}
                          </p>
                          {sub.due_at && (
                            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                              Due: {formatDate(sub.due_at)}
                            </p>
                          )}

                          {/* Status update */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Status:</label>
                            <select
                              className="input"
                              value={subStatus[sub.id] ?? sub.status}
                              onChange={e => setSubStatus(s => ({ ...s, [sub.id]: e.target.value }))}
                              style={{ width: 'auto', fontSize: 13, padding: '4px 8px' }}
                            >
                              {['open','in_progress','resolved','closed'].map(s => (
                                <option key={s} value={s}>{s.replace('_',' ')}</option>
                              ))}
                            </select>
                            {(subStatus[sub.id] && subStatus[sub.id] !== sub.status) && (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => updateSubStatus(sub.id, subStatus[sub.id])}
                              >
                                Update
                              </button>
                            )}
                          </div>

                          {/* Messages */}
                          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
                            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Thread</h4>
                            {(subMessages[sub.id] || []).length === 0 ? (
                              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12, fontStyle: 'italic' }}>No messages yet.</p>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                                {(subMessages[sub.id] || []).map(m => (
                                  <div key={m.id} style={{ display: 'flex', gap: 10, flexDirection: m.sender_id === user.id ? 'row-reverse' : 'row' }}>
                                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--action-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                                      {m.sender_name?.[0]?.toUpperCase()}
                                    </div>
                                    <div style={{ maxWidth: '75%' }}>
                                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3, textAlign: m.sender_id === user.id ? 'right' : 'left' }}>
                                        {m.sender_name} · {timeAgo(m.created_at)}
                                      </div>
                                      <div style={{ background: m.sender_id === user.id ? 'var(--action-bg)' : 'var(--n-100)', padding: '8px 12px', borderRadius: 8, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                                        {m.content}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Send message */}
                            <div style={{ display: 'flex', gap: 8 }}>
                              <input
                                className="input"
                                placeholder="Reply to sub-ticket…"
                                value={subMsg}
                                onChange={e => setSubMsg(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendSubMessage(sub.id); } }}
                                style={{ flex: 1, fontSize: 13 }}
                              />
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => sendSubMessage(sub.id)}
                                disabled={!subMsg.trim()}
                              >
                                Send
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div style={{ position: 'relative', minWidth: 0 }}>
          {/* FAQ overlay panel — slides over the sidebar */}
          {showFaq && isAgent && feature('knowledge.enabled') && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,.12)', display: 'flex', flexDirection: 'column', minHeight: 500, maxHeight: '80vh', overflow: 'hidden' }}>
              <FAQPanel onClose={() => setShowFaq(false)} productId={ticketProductId} productName={ticket.product_name} />
            </div>
          )}
          {isAgent && (
            <div className="card" style={{ padding: 20, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Ticket Management</h3>

              {/* Status — editable for all agents, auto-focused, Tab → remarks */}
              <div className="form-group">
                <label className="label">Status</label>
                <select
                  ref={statusRef}
                  className="input"
                  value={ticket.status}
                  onChange={e => updateField('status', e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Tab') {
                      e.preventDefault();
                      remarkRef.current?.focus();
                    }
                  }}
                >
                  {STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                </select>
              </div>

              {/* Priority — display only for pure agent */}
              <div className="form-group">
                <label className="label">Priority</label>
                {isPureAgent ? (
                  <div style={{
                    padding: '7px 12px', borderRadius: 8,
                    border: '1.5px solid var(--border-subtle)',
                    background: 'var(--bg-app)', fontSize: 13,
                    color: 'var(--text-secondary)', fontWeight: 600,
                    textTransform: 'capitalize',
                  }}>
                    {ticket.priority}
                  </div>
                ) : (
                  <select className="input" value={ticket.priority} onChange={e => updateField('priority', e.target.value)}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                )}
              </div>

              {/* Category — display only for pure agent */}
              <div className="form-group">
                <label className="label">Category</label>
                {isPureAgent ? (
                  <div style={{
                    padding: '7px 12px', borderRadius: 8,
                    border: '1.5px solid var(--border-subtle)',
                    background: 'var(--bg-app)', fontSize: 13,
                    color: 'var(--text-secondary)', fontWeight: 600,
                    textTransform: 'capitalize',
                  }}>
                    {ticket.category?.replace('-', ' ') || '—'}
                  </div>
                ) : (
                  <select className="input" value={ticket.category} onChange={e => updateField('category', e.target.value)}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('-',' ')}</option>)}
                  </select>
                )}
              </div>

              {/* Assigned To — display only for pure agent */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">Assigned To</label>
                {isPureAgent ? (
                  <div style={{
                    padding: '7px 12px', borderRadius: 8,
                    border: '1.5px solid var(--border-subtle)',
                    background: 'var(--bg-app)', fontSize: 13,
                    color: 'var(--text-secondary)', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    {ticket.agent_name || <span style={{ color: '#f59e0b' }}>Unassigned</span>}
                    {ticket.agent_tier && <TierBadge tier={ticket.agent_tier} inline />}
                  </div>
                ) : (
                  <>
                    <label className="label" style={{ marginBottom: 6 }}>
                      {ticket.product_name && capableAgents.length > 0 && capableAgents.length < agents.length && (
                        <span style={{ marginLeft: 7, fontSize: 10, background: 'var(--blue-100)', color: 'var(--blue-700)', padding: '2px 7px', borderRadius: 999, fontWeight: 600, letterSpacing: .2 }}>
                          Filtered by product
                        </span>
                      )}
                    </label>
                    {ticket.assigned_to && ticket.agent_tier && <TierBadge tier={ticket.agent_tier} />}
                    <select
                      className="input"
                      value={ticket.assigned_to || ''}
                      onChange={e => updateField('assigned_to', e.target.value || null)}
                      style={{ marginTop: ticket.assigned_to ? 8 : 0 }}
                    >
                      <option value="">— Unassigned —</option>
                      {(() => {
                        const pool = capableAgents.length > 0 ? capableAgents : agents;
                        const tiers = ['L1', 'L2', 'L3', 'Other'];
                        function tierForAgent(agent) {
                          if (ticketProductId && Array.isArray(agent.agent_products)) {
                            const entry = agent.agent_products.find(p => p.product_id === ticketProductId);
                            if (entry) return entry.tier || 'L1';
                          }
                          return 'Other';
                        }
                        return tiers.map(tier => {
                          const group = pool.filter(a => tierForAgent(a) === tier);
                          if (!group.length) return null;
                          return (
                            <optgroup key={tier} label={`── ${tier} Support ──`}>
                              {group.map(a => <option key={a.id} value={a.id}>{a.name}{isAgentOOF(a) ? "  🌴 OOF" : ""}</option>)}
                            </optgroup>
                          );
                        });
                      })()}
                    </select>
                    {ticket.product_name && capableAgents.length === 0 && agents.length > 0 && (
                      <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                        No agents assigned to this product — showing all agents.
                      </p>
                    )}
                    {ticket.agent_tier === 'L1' && ticketProductId && capableAgents.some(a =>
                      Array.isArray(a.agent_products) && a.agent_products.some(p => p.product_id === ticketProductId && p.tier === 'L2')
                    ) && (
                      <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--amber-50)', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                        <strong>Escalate to L2?</strong> Select an L2 agent above to escalate this ticket.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Internal team chat — visible to agents, managers, admins */}
          {isAgent && feature('team_chat.enabled') && (
            <InternalAgentChat currentUser={user} token={token} />
          )}

          {/* Ticket Info — hidden only for pure agent role */}
          {user.role !== 'agent' && <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Ticket Info</h3>
            {[
              ['Complaint No.', ticket.ticket_number],
              ['Customer', ticket.customer_name],
              ['Email', ticket.customer_email],
              ...(ticket.product_name ? [['Product', ticket.product_name]] : []),
              ...(ticket.serial_number ? [['Serial No.', ticket.serial_number]] : []),
              ['Raised By', ticket.contact_name || ticket.raised_by_name || '—'],
              ['Phone', ticket.contact_mobile || ticket.customer_phone || '—'],
              ...(ticket.issue_type ? [['Issue Type', ticket.issue_type]] : []),
              ...(ticket.location ? [['Location', ticket.location]] : []),
              ...(ticket.started_on ? [['Started On', ticket.started_on]] : []),
              ...(ticket.is_recurring ? [['Recurring', 'Yes']] : []),
              ['Assigned To', ticket.agent_name
                ? <span style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                    {ticket.agent_name}
                    {ticket.agent_tier && <TierBadge tier={ticket.agent_tier} inline />}
                  </span>
                : <span style={{ color: '#f59e0b', fontWeight: 500 }}>Unassigned</span>
              ],
              ['Created', formatDate(ticket.created_at)],
              ['Updated', formatDate(ticket.updated_at)],
              ['Resolved', ticket.resolved_at ? formatDate(ticket.resolved_at) : '—'],
              ...(ticket.closed_at ? [['Closed', formatDate(ticket.closed_at)]] : []),
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 10 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{k}</span>
                <span style={{ fontWeight: 500, textAlign: 'right', maxWidth: 160, wordBreak: 'break-all' }}>{v}</span>
              </div>
            ))}

            {/* Close reason & remark — shown when ticket is closed */}
            {ticket.status === 'closed' && ticket.close_reason && (
              <div style={{ marginTop: 14, borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Resolution</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {ticket.resolution_outcome && <span style={{ display: 'inline-flex', alignItems: 'center', background: '#e8f5ee', color: 'var(--status-resolved-text)', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>{ticket.resolution_outcome}</span>}
                  <span style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--action-bg)', color: 'var(--status-resolved-text)', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700, textTransform: 'capitalize' }}>{ticket.close_reason}</span>
                  {!!ticket.customer_confirmed && <span style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--n-100)', color: 'var(--text-secondary)', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>✓ Customer confirmed</span>}
                </div>
                {ticket.root_cause && (
                  <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, margin: '0 0 6px' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>Root cause:</strong> {ticket.root_cause}
                  </p>
                )}
                {ticket.close_remark && (
                  <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' }}>
                    {ticket.close_remark}
                  </p>
                )}
                {ticket.rma_ref && <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0 0' }}>RMA: {ticket.rma_ref}</p>}
                {ticket.preventive_action && <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0 0' }}><strong>Preventive:</strong> {ticket.preventive_action}</p>}
                {ticket.time_spent && <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '6px 0 0' }}>Time spent: {ticket.time_spent}</p>}
              </div>
            )}
          </div>}

          {user.role === 'super_admin' && (
            <div className="card" style={{ padding: 16, marginTop: 16, border: '1px solid #fca5a5' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 8 }}>Danger zone</div>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 10px', lineHeight: 1.5 }}>Delete a wrongly-raised ticket. It is removed from all lists and reports (kept only for audit).</p>
              <button onClick={() => { setDeleteReason(''); setDeleteErr(''); setShowDeleteModal(true); }} className="btn btn-danger" style={{ width: '100%' }}>🗑 Delete ticket</button>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* ── Delete Ticket Modal (super admin) ──────────────────────────── */}
    {showDeleteModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
        <div className="card" style={{ width: '100%', maxWidth: 440, padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>Delete ticket</h2>
            <button onClick={() => setShowDeleteModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
            This hides the ticket from <strong>every list, report and screen</strong>. It's retained only for audit. Give a reason for deleting it.
          </p>
          {deleteErr && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{deleteErr}</div>}
          <form onSubmit={handleDeleteTicket}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Reason <span style={{ color: '#dc2626' }}>*</span></label>
            <textarea className="input" autoFocus rows={3} placeholder="e.g. Duplicate / test ticket raised by mistake"
              value={deleteReason} onChange={e => setDeleteReason(e.target.value)} style={{ resize: 'vertical', fontSize: 13 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-danger" disabled={deleteBusy || !deleteReason.trim()}>{deleteBusy ? 'Deleting…' : 'Delete ticket'}</button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* ── Stop SLA Modal ─────────────────────────────────────────────── */}
    {showSlaStop && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
        <div className="card" style={{ width: '100%', maxWidth: 460, padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>Stop the SLA clock</h2>
            <button onClick={() => setShowSlaStop(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
            Use this only when the action is <strong>pending with the client</strong>. The SLA clock freezes and resumes automatically when the customer replies (or when you resume it). This is logged in the activity trail.
          </p>
          {slaErr && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{slaErr}</div>}
          <form onSubmit={stopSla}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Reason (optional)</label>
            <textarea className="input" rows={2} placeholder="e.g. Awaiting VPN access from customer's IT"
              value={slaReason} onChange={e => setSlaReason(e.target.value)} style={{ resize: 'vertical', fontSize: 13, marginBottom: 14 }} />
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Type <strong>STOP</strong> to confirm</label>
            <input className="input" autoFocus value={slaConfirm} onChange={e => setSlaConfirm(e.target.value)} placeholder="STOP" style={{ fontSize: 13 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowSlaStop(false)}>Cancel</button>
              <button type="submit" className="btn btn-danger" disabled={slaBusy || slaConfirm.trim().toUpperCase() !== 'STOP'}>{slaBusy ? 'Stopping…' : 'Stop SLA clock'}</button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* ── Reopen Ticket Modal ────────────────────────────────────────── */}
    {showReopen && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
        <div className="card" style={{ width: '100%', maxWidth: 460, padding: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>Reopen ticket</h2>
            <button onClick={() => setShowReopen(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Tell us why this needs to be reopened — it will be shared with the support agent.</p>
          {reopenErr && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{reopenErr}</div>}
          <form onSubmit={handleReopen}>
            <textarea className="input" rows={4} autoFocus placeholder="e.g. The issue came back after two days…"
              value={reopenReason} onChange={e => setReopenReason(e.target.value)} style={{ resize: 'vertical', fontSize: 13 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowReopen(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={reopenBusy || !reopenReason.trim()} style={{ background: '#0B6623' }}>{reopenBusy ? 'Reopening…' : 'Reopen ticket'}</button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* ── Close Ticket Modal ─────────────────────────────────────────── */}
    {showCloseModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
        <div className="card" style={{ width: '100%', maxWidth: 480, padding: 0, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 28px 14px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Close Ticket</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>This will be saved for knowledge management.</p>
            </div>
            <button onClick={() => setShowCloseModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          {closeError && (
            <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, margin: '14px 28px 0', fontSize: 13, flexShrink: 0 }}>
              {closeError}
            </div>
          )}

          <form onSubmit={handleCloseTicket} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            <div style={{ overflowY: 'auto', padding: '18px 28px 8px', flex: 1, minHeight: 0 }}>
            {/* Resolution outcome */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10 }}>
                Outcome <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {RESOLUTION_OUTCOMES.map(o => (
                  <button key={o} type="button" onClick={() => setResolutionOutcome(o)}
                    style={{ padding: '7px 13px', borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                      border: resolutionOutcome === o ? '1.5px solid var(--status-resolved-text)' : '1.5px solid var(--border-subtle)',
                      background: resolutionOutcome === o ? '#e8f5ee' : 'var(--bg-surface)',
                      color: resolutionOutcome === o ? 'var(--status-resolved-text)' : 'var(--text-muted)' }}>
                    {o}
                  </button>
                ))}
              </div>
            </div>

            {/* RMA reference — only for device replacement */}
            {resolutionOutcome === 'Device replaced (RMA)' && (
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>RMA / replacement reference</label>
                <input className="input" value={rmaRef} onChange={e => setRmaRef(e.target.value)} placeholder="RMA no. / new device serial" style={{ fontSize: 13 }} />
              </div>
            )}

            {/* Resolution category */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10 }}>
                Resolution Category <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {CLOSE_REASONS.map(r => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setCloseReason(r.value)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: closeReason === r.value ? '2px solid var(--status-resolved-text)' : '1px solid var(--border-subtle)',
                      background: closeReason === r.value ? '#e8f5ee' : 'var(--bg-surface)',
                      color: closeReason === r.value ? 'var(--status-resolved-text)' : 'var(--text-muted)',
                      fontWeight: closeReason === r.value ? 700 : 500,
                      fontSize: 13,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all .15s',
                    }}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Root cause */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                Root Cause <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea className="input" rows={2} placeholder="Why did it happen? e.g. Faulty SFP module on the branch router"
                value={rootCause} onChange={e => setRootCause(e.target.value)} style={{ resize: 'vertical', fontSize: 13 }} />
            </div>

            {/* Closing remark */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                Resolution Summary / Action Taken <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                className="input"
                rows={4}
                placeholder="What you did to resolve it — this will be used as a knowledge base entry…"
                value={closeRemark}
                onChange={e => setCloseRemark(e.target.value)}
                style={{ resize: 'vertical', fontSize: 13 }}
              />
            </div>

            {/* Confirmation & follow-up */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Customer confirmed?</label>
                <div style={{ display: 'inline-flex', background: 'var(--n-100)', borderRadius: 8, padding: 3, gap: 2 }}>
                  {[[1, 'Yes'], [0, 'No']].map(([v, lbl]) => (
                    <button key={v} type="button" onClick={() => setCustomerConfirmed(v)}
                      style={{ border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, padding: '6px 14px', borderRadius: 6,
                        background: customerConfirmed === v ? 'var(--bg-surface)' : 'transparent',
                        color: customerConfirmed === v ? '#0B6623' : 'var(--text-secondary)',
                        boxShadow: customerConfirmed === v ? '0 1px 2px rgba(0,0,0,.1)' : 'none' }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Time spent <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional)</span></label>
                <input className="input" value={timeSpent} onChange={e => setTimeSpent(e.target.value)} placeholder="e.g. 45m / 2h" style={{ fontSize: 13 }} />
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                Preventive action / follow-up <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(optional{ticket.is_recurring ? ' — recommended, this was flagged recurring' : ''})</span>
              </label>
              <textarea className="input" rows={2} placeholder="Permanent fix or follow-up needed to stop recurrence"
                value={preventiveAction} onChange={e => setPreventiveAction(e.target.value)} style={{ resize: 'vertical', fontSize: 13 }} />
            </div>

            {/* Add to FAQ toggle */}
            {feature('knowledge.enabled') && (
            <div style={{ marginBottom: 18 }}>
              <button
                type="button"
                onClick={() => setAddToFaq(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: addToFaq ? 'var(--action-bg)' : 'var(--bg-app)',
                  border: addToFaq ? '1.5px solid var(--action-hover)' : '1.5px solid var(--border-subtle)',
                  borderRadius: 8, padding: '9px 14px', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, color: addToFaq ? 'var(--status-resolved-text)' : 'var(--text-secondary)',
                  width: '100%', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 16 }}>📚</span>
                {addToFaq ? 'Adding to FAQ (pending super admin approval)' : 'Add this resolution to FAQ?'}
                <span style={{ marginLeft: 'auto', fontSize: 11 }}>{addToFaq ? '▲ Hide' : '▼ Expand'}</span>
              </button>

              {addToFaq && (
                <div style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: '0 0 8px 8px', padding: 16, marginTop: -1 }}>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
                      Question <span style={{ color: '#ef4444' }}>*</span>
                      <span style={{ fontWeight: 400, color: faqWordCount(faqQuestion) > 50 ? '#ef4444' : 'var(--text-secondary)', marginLeft: 8 }}>
                        {faqWordCount(faqQuestion)}/50 words
                      </span>
                    </label>
                    <textarea
                      className="input"
                      rows={2}
                      placeholder="e.g. How do I reset the device after a firmware update?"
                      value={faqQuestion}
                      onChange={e => setFaqQuestion(e.target.value)}
                      style={{ resize: 'vertical', fontSize: 13, borderColor: faqWordCount(faqQuestion) > 50 ? '#ef4444' : undefined }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
                      Answer <span style={{ color: '#ef4444' }}>*</span>
                      <span style={{ fontWeight: 400, color: faqWordCount(faqAnswer) > 200 ? '#ef4444' : 'var(--text-secondary)', marginLeft: 8 }}>
                        {faqWordCount(faqAnswer)}/200 words
                      </span>
                    </label>
                    <textarea
                      className="input"
                      rows={4}
                      placeholder="Provide a clear, step-by-step answer…"
                      value={faqAnswer}
                      onChange={e => setFaqAnswer(e.target.value)}
                      style={{ resize: 'vertical', fontSize: 13, borderColor: faqWordCount(faqAnswer) > 200 ? '#ef4444' : undefined }}
                    />
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 8, marginBottom: 0 }}>
                    This will be sent to the super admin for review before appearing in the public FAQ.
                  </p>
                </div>
              )}
            </div>
            )}

            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '14px 28px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', flexShrink: 0 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCloseModal(false)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={closeSaving}
                style={{ background: '#0B6623' }}
              >
                {closeSaving ? 'Closing…' : '✓ Close Ticket'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
}

// ── Tier badge: shows L1 / L2 / L3 with colour coding ──────────────────────
function TierBadge({ tier, inline = false }) {
  const styles = {
    L1: { bg: 'var(--action-bg)', color: 'var(--status-resolved-text)', border: 'var(--action-hover)' },
    L2: { bg: 'var(--blue-100)', color: 'var(--blue-700)', border: 'var(--blue-200)' },
    L3: { bg: '#ede9fe', color: '#6d28d9', border: '#c4b5fd' },
    Other: { bg: 'var(--n-100)', color: 'var(--text-muted)', border: 'var(--n-300)' },
  };
  const s = styles[tier] || styles['Other'];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: inline ? 10 : 11,
      fontWeight: 700,
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
      borderRadius: 999,
      padding: inline ? '1px 6px' : '3px 9px',
      letterSpacing: .3,
      whiteSpace: 'nowrap',
    }}>
      {tier}
    </span>
  );
}
